/**
 * The host picker (spec 032): a host app opens `/picker?origin=…&kinds=…` as a
 * popup; the author browses their folders, stories and scenes, signs in
 * inline if needed, and picks one item. The page posts exactly one message to
 * `window.opener` (`smPickerSelect` or `smPickerCancel`) and closes. Every URL
 * in the selection is the Service's; the picker assembles none.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import type {
  Folder,
  PublishedSceneSummary,
  PublishedStorySummary,
  SceneApiResponse,
  StoryApiResponse,
} from '../../../Shared/src/@types';
import { breadcrumbPath, sortByName } from '../components/organiser/organiser';
import styles from './Picker.module.scss';

export type PickerKind = 'story' | 'scene';

/** The selection message (spec 032). `storyId`/`storyName` only for a story. */
export interface PickerSelectMessage {
  type: 'smPickerSelect';
  kind: PickerKind;
  id: number;
  name: string;
  capabilityUrl: string;
  version: number;
  publishedAt: string;
  editUrl: string;
  storyId?: number;
  storyName?: string;
}

const NO_OPENER = 'Open this page from the app you are picking for.';
const SESSION_ENDED = 'Your session ended. Sign in again.';

/** `?origin=` reduced to its origin; missing or not http(s): `'*'` with a warning. */
export function parsePickerOrigin(raw: string | null): string {
  if (!raw) {
    console.warn('[Picker] No ?origin parameter; postMessage will target *');
    return '*';
  }
  try {
    const url = new URL(raw);
    if (url.protocol === 'http:' || url.protocol === 'https:') return url.origin;
  } catch {
    /* falls through */
  }
  console.warn('[Picker] Malformed ?origin value; postMessage will target *');
  return '*';
}

/** `?kinds=story,scene`: the selectable kinds; unknown tokens ignored, none means both. */
export function parsePickerKinds(raw: string | null): Set<PickerKind> {
  const kinds = new Set<PickerKind>();
  for (const token of (raw ?? '').split(',')) {
    const t = token.trim().toLowerCase();
    if (t === 'story' || t === 'scene') kinds.add(t);
  }
  return kinds.size > 0 ? kinds : new Set<PickerKind>(['story', 'scene']);
}


/**
 * The host's name for the copy (spec 032): `origin`'s host with its port, or
 * `null` when `origin` fell back to `'*'`.
 */
export function hostLabelOf(origin: string): string | null {
  if (origin === '*') return null;
  try {
    return new URL(origin).host || null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Icons: inline stroke SVGs, decorative (the text next to each says it all).
// ---------------------------------------------------------------------------

type IconName = 'brand' | 'home' | 'folder' | 'story' | 'scene' | 'search' | 'clear' | 'check' | 'alert' | 'external' | 'info';

const ICON_PATHS: Record<IconName, React.ReactNode> = {
  brand: <path d="M4 5h16v11H8l-4 4z" />,
  home: <path d="M3 11 12 4l9 7v9h-6v-6H9v6H3z" />,
  folder: <path d="M3 6h6l2 2h10v11H3z" />,
  story: (
    <>
      <circle cx="6" cy="6" r="2.5" />
      <circle cx="18" cy="6" r="2.5" />
      <circle cx="12" cy="18" r="2.5" />
      <path d="M6 8.5v2a3 3 0 0 0 3 3h6a3 3 0 0 0 3-3v-2M12 13.5v2" />
    </>
  ),
  scene: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m10 9 5 3-5 3z" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </>
  ),
  clear: <path d="M6 6l12 12M18 6 6 18" />,
  check: <path d="m5 12 5 5 9-10" />,
  alert: (
    <>
      <path d="M12 3 2 20h20z" />
      <path d="M12 10v4M12 17v.5" />
    </>
  ),
  external: <path d="M14 4h6v6M20 4l-9 9M18 14v6H4V6h6" />,
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v6M12 7.5v.5" />
    </>
  ),
};

const Icon: React.FC<{ name: IconName; size?: number; strokeWidth?: number; className?: string }> = ({
  name,
  size = 18,
  strokeWidth = 2,
  className,
}) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {ICON_PATHS[name]}
  </svg>
);

const Eyebrow: React.FC = () => (
  <div className={styles.eyebrow}>
    <Icon name="brand" size={14} strokeWidth={2.2} />
    Slizzart Stories
  </div>
);

// ---------------------------------------------------------------------------
// Inline sign-in (001): never navigates, so `origin` and `kinds` survive.
// ---------------------------------------------------------------------------

interface LoginFormProps {
  notice?: string;
  hostLabel: string | null;
  onCancel: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ notice, hostLabel, onCancel }) => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.signIn}>
      <div className={styles.signInBrand}>
        <Eyebrow />
        {hostLabel && (
          <p className={styles.signInFor}>
            for <span className={styles.hostName}>{hostLabel}</span>
          </p>
        )}
      </div>
      <form className={styles.loginForm} onSubmit={handleSubmit} noValidate>
        <h1 className={styles.loginTitle}>Sign in to choose content</h1>
        <p className={styles.loginSubtitle}>
          Your Slizzart Stories account stays private. {hostLabel ?? 'The app'} only receives the story or scene you pick.
        </p>
        {notice && (
          <p className={styles.notice} role="status">
            <Icon name="info" size={16} />
            {notice}
          </p>
        )}

        <label className={styles.label} htmlFor="picker-email">
          Email
          <input
            id="picker-email"
            type="email"
            className={styles.input}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
        </label>

        <label className={styles.label} htmlFor="picker-password">
          Password
          <input
            id="picker-password"
            type="password"
            className={styles.input}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
          />
        </label>

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <button type="submit" className={`${styles.primaryBtn} ${styles.fullWidth}`} disabled={busy} aria-busy={busy}>
          {busy && <span className={styles.btnSpinner} aria-hidden="true" />}
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        <button type="button" className={`${styles.plainBtn} ${styles.fullWidth}`} onClick={onCancel}>
          Cancel
        </button>
      </form>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

interface PickerData {
  folders: Folder[];
  stories: StoryApiResponse[];
  scenes: SceneApiResponse[];
  publishedStories: Map<number, PublishedStorySummary>;
  publishedScenes: Map<number, PublishedSceneSummary>;
}

interface Row {
  kind: PickerKind;
  id: number;
  name: string;
  folderId: number | null;
  published?: PublishedStorySummary;
}

interface PublishFailure {
  reasons: string[];
  editUrl?: string;
}

type KindTab = 'all' | PickerKind;

const rowKey = (kind: PickerKind, id: number) => `${kind}-${id}`;

type RowTone = 'published' | 'unpublished' | 'missing';

/** "Published v3 · 20 Oct 2026", "Not published yet", or "Files missing, will republish". */
function rowStatus(row: Row): { tone: RowTone; text: string } {
  const pub = row.published;
  if (!pub) return { tone: 'unpublished', text: 'Not published yet' };
  if (pub.filesPresent === false) return { tone: 'missing', text: 'Files missing, will republish' };
  const date = new Date(pub.publishedAt);
  const when = Number.isNaN(date.getTime())
    ? ''
    : ` · ${date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}`;
  return { tone: 'published', text: `Published v${pub.version}${when}` };
}

const TONE_CLASS: Record<RowTone, string> = {
  published: styles.statusPublished,
  unpublished: styles.statusUnpublished,
  missing: styles.statusMissing,
};

/** Published and its files are here: "Select"; else "Publish and select" (D-1). */
const isReady = (row: Row) => !!row.published && row.published.filesPresent !== false;

function failureOf(res: { error?: string; errors?: unknown; missingMediaIds?: unknown; editUrl?: unknown }): PublishFailure {
  const reasons: string[] = [];
  if (Array.isArray(res.errors) && res.errors.length > 0) reasons.push(...res.errors.map(String));
  else reasons.push(res.error || 'Publish failed');
  if (Array.isArray(res.missingMediaIds) && res.missingMediaIds.length > 0) {
    reasons.push(`Missing media ids: ${res.missingMediaIds.join(', ')}`);
  }
  return { reasons, editUrl: typeof res.editUrl === 'string' ? res.editUrl : undefined };
}

/** `name` with the first case-insensitive match of `query` in a `<mark>`. */
function highlight(name: string, query: string): React.ReactNode {
  const q = query.trim();
  const at = q ? name.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (at < 0) return name;
  return (
    <>
      {name.slice(0, at)}
      <mark className={styles.mark}>{name.slice(at, at + q.length)}</mark>
      {name.slice(at + q.length)}
    </>
  );
}

const itemCount = (n: number) => (n === 1 ? '1 item' : `${n} items`);
const resultCount = (n: number) => (n === 1 ? '1 result in all folders' : `${n} results in all folders`);

const TABS: ReadonlyArray<readonly [KindTab, string]> = [
  ['all', 'All'],
  ['story', 'Stories'],
  ['scene', 'Scenes'],
];

// ---------------------------------------------------------------------------
// Picker page
// ---------------------------------------------------------------------------

const Picker: React.FC = () => {
  const { user, status } = useAuth();
  const [searchParams] = useSearchParams();
  const origin = useMemo(() => parsePickerOrigin(searchParams.get('origin')), [searchParams]);
  const hostLabel = useMemo(() => hostLabelOf(origin), [origin]);
  const kinds = useMemo(() => parsePickerKinds(searchParams.get('kinds')), [searchParams]);
  const bothKinds = kinds.size === 2;
  // Read once: a window without an opener has nobody to pick for.
  const [opener] = useState<Window | null>(() => (typeof window !== 'undefined' ? window.opener : null));

  const [data, setData] = useState<PickerData | null>(null);
  const [fetchError, setFetchError] = useState('');
  const [folderId, setFolderId] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState<KindTab>('all');
  const [selected, setSelected] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [failure, setFailure] = useState<PublishFailure | null>(null);
  const [sessionEnded, setSessionEnded] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  // At most one message per window (spec 032): a select or cancel suppresses
  // the pagehide cancel that `window.close()` would otherwise send.
  const posted = useRef(false);
  const post = useCallback(
    (message: PickerSelectMessage | { type: 'smPickerCancel' }, close = true) => {
      if (posted.current) return;
      posted.current = true;
      opener?.postMessage(message, origin);
      if (close) window.close();
    },
    [opener, origin]
  );
  const cancel = useCallback(() => post({ type: 'smPickerCancel' }), [post]);

  // A session that ends while open (D-2): AuthContext stays on /picker, the
  // inline form says why.
  const wasSignedIn = useRef(false);
  useEffect(() => {
    if (status === 'signedIn') {
      wasSignedIn.current = true;
      setSessionEnded(false);
    } else if (status === 'signedOut' && wasSignedIn.current) {
      setSessionEnded(true);
    }
  }, [status]);

  // Load (and reload after every sign-in). Abortable, so StrictMode and a
  // sign-out mid-load leave no stale results.
  useEffect(() => {
    if (!opener || !user) return;
    const controller = new AbortController();
    const { signal } = controller;
    setFetchError('');
    Promise.all([
      apiClient.getFolders({ signal }),
      apiClient.getStories({ signal }),
      apiClient.getScenes({ signal }),
      apiClient.getPublishedStories({ signal }),
      apiClient.getPublishedScenes({ signal }),
    ])
      .then(([folders, stories, scenes, pubStories, pubScenes]) => {
        if (signal.aborted) return;
        const failed = [folders, stories, scenes, pubStories, pubScenes].find((r) => !r.success);
        if (failed || !folders.success || !stories.success || !scenes.success || !pubStories.success || !pubScenes.success) {
          setFetchError((failed && !failed.success && failed.error) || 'Failed to load your content');
          return;
        }
        setData({
          folders: folders.data,
          stories: stories.data,
          scenes: scenes.data,
          publishedStories: new Map(pubStories.data.map((p) => [p.id, p])),
          publishedScenes: new Map(pubScenes.data.map((p) => [p.id, p])),
        });
      })
      .catch((err: unknown) => {
        if (signal.aborted) return;
        setFetchError(err instanceof Error ? err.message : 'Network error');
      });
    return () => controller.abort();
  }, [opener, user]);

  // Escape clears a non-empty search first, else cancels; closing the window
  // by other means cancels, best effort.
  const queryRef = useRef(query);
  queryRef.current = query;
  useEffect(() => {
    if (!opener) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (queryRef.current.trim().length > 0) {
        e.preventDefault();
        setQuery('');
        return;
      }
      cancel();
    };
    const onPageHide = () => post({ type: 'smPickerCancel' }, false);
    window.addEventListener('keydown', onKey);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [opener, cancel, post]);

  // Every story and scene the picker can list. Scenes a story owns are not
  // items of their own (002); GET /scenes leaves them out, this makes sure.
  const rows = useMemo<Row[]>(() => {
    if (!data) return [];
    const owned = new Set<number>();
    for (const story of data.stories) {
      for (const ss of story.storyScenes ?? []) if (ss.mode === 'owned') owned.add(ss.sceneId);
    }
    const stories: Row[] = data.stories.map((s) => ({
      kind: 'story',
      id: s.id,
      name: s.name,
      folderId: s.folderId ?? null,
      published: data.publishedStories.get(s.id),
    }));
    const scenes: Row[] = data.scenes
      .filter((s) => !owned.has(s.id))
      .map((s) => ({
        kind: 'scene',
        id: s.id,
        name: s.name,
        folderId: s.folderId ?? null,
        published: data.publishedScenes.get(s.id),
      }));
    return [...stories, ...scenes];
  }, [data]);

  // Keep the selection across a reload only while that item is still listed.
  useEffect(() => {
    if (data && selected && !rows.some((r) => rowKey(r.kind, r.id) === selected)) setSelected(null);
  }, [data, rows, selected]);

  const folders = useMemo(() => data?.folders ?? [], [data]);
  const searching = query.trim().length > 0;

  // A folder's item count: its direct subfolders, stories and listed scenes.
  const counts = useMemo(() => {
    const map = new Map<number, number>();
    const bump = (id: number | null) => {
      if (id !== null) map.set(id, (map.get(id) ?? 0) + 1);
    };
    for (const f of folders) bump(f.parentId ?? null);
    for (const r of rows) bump(r.folderId);
    return map;
  }, [folders, rows]);

  const visibleFolders = searching ? [] : sortByName(folders.filter((f) => (f.parentId ?? null) === folderId));
  const { storyRows, sceneRows } = useMemo(() => {
    const lower = query.trim().toLowerCase();
    const pool = searching
      ? rows.filter((r) => (r.name ?? '').toLowerCase().includes(lower))
      : rows.filter((r) => r.folderId === folderId);
    const showStories = !bothKinds || tab !== 'scene';
    const showScenes = !bothKinds || tab !== 'story';
    return {
      storyRows: showStories ? sortByName(pool.filter((r) => r.kind === 'story')) : [],
      sceneRows: showScenes ? sortByName(pool.filter((r) => r.kind === 'scene')) : [],
    };
  }, [rows, searching, query, folderId, tab, bothKinds]);

  const folderPathText = (id: number | null) => {
    const path = breadcrumbPath(folders, id);
    return path.length ? path.map((f) => f.name).join(' › ') : 'All content';
  };

  const selectedRow = rows.find((r) => rowKey(r.kind, r.id) === selected) ?? null;

  const choose = (row: Row) => {
    if (!kinds.has(row.kind) || publishing) return;
    const key = rowKey(row.kind, row.id);
    if (key === selected) return; // "Try again" stays until the selection changes
    setSelected(key);
    setFailure(null);
  };

  const clearSearch = () => {
    setQuery('');
    searchRef.current?.focus();
  };

  const messageFor = (
    row: Row,
    pub: { capabilityUrl: string; version: number; publishedAt: string; editUrl: string }
  ): PickerSelectMessage => ({
    type: 'smPickerSelect',
    kind: row.kind,
    id: row.id,
    name: row.name,
    capabilityUrl: pub.capabilityUrl,
    version: pub.version,
    publishedAt: pub.publishedAt,
    editUrl: pub.editUrl,
    // Hosts written against 011 v5 read these.
    ...(row.kind === 'story' ? { storyId: row.id, storyName: row.name } : {}),
  });

  const confirm = async () => {
    const row = selectedRow;
    if (!row || publishing || !kinds.has(row.kind)) return;
    if (isReady(row)) {
      post(messageFor(row, row.published!));
      return;
    }
    // D-1: publish with the author's session, then select.
    setPublishing(true);
    setFailure(null);
    try {
      const res = row.kind === 'story' ? await apiClient.publishStory(row.id) : await apiClient.publishScene(row.id);
      if (res.success) {
        post(messageFor(row, res.data));
      } else {
        const failed = failureOf(res as Parameters<typeof failureOf>[0]);
        setFailure({ ...failed, editUrl: failed.editUrl ?? row.published?.editUrl });
      }
    } catch (err) {
      setFailure({ reasons: [err instanceof Error ? err.message : 'Publish failed'], editUrl: row.published?.editUrl });
    } finally {
      setPublishing(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────

  if (!opener) {
    return (
      <div className={styles.page}>
        <div className={styles.centered}>
          <Eyebrow />
          <p className={styles.empty} role="alert">
            {NO_OPENER}
          </p>
        </div>
      </div>
    );
  }

  if (status === 'checking') {
    return (
      <div className={styles.page}>
        <div className={styles.spinner} role="status">
          <span className={styles.srOnly}>Loading…</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className={styles.page}>
        <LoginForm notice={sessionEnded ? SESSION_ENDED : undefined} hostLabel={hostLabel} onCancel={cancel} />
      </div>
    );
  }

  const onlyKind: PickerKind | null = bothKinds ? null : kinds.has('story') ? 'story' : 'scene';
  const title = onlyKind === 'story' ? 'Choose a story' : onlyKind === 'scene' ? 'Choose a scene' : 'Choose a story or scene';
  const pickHint = onlyKind === 'story' ? 'Pick one story' : onlyKind === 'scene' ? 'Pick one scene' : 'Pick one story or scene';
  const path = breadcrumbPath(folders, folderId);
  const needsPublish = !!selectedRow && !isReady(selectedRow);
  const confirmLabel = publishing ? 'Publishing…' : failure ? 'Try again' : needsPublish ? 'Publish and select' : 'Select';
  const hostOrApp = hostLabel ?? 'the app';
  const kindWord = selectedRow?.kind === 'scene' ? 'scene' : 'story';
  const resultTotal = storyRows.length + sceneRows.length;
  const nothingListed = !!data && visibleFolders.length === 0 && resultTotal === 0;

  const renderRow = (row: Row) => {
    const key = rowKey(row.kind, row.id);
    const allowed = kinds.has(row.kind);
    const isSelected = selected === key;
    const st = rowStatus(row);
    return (
      <li
        key={key}
        className={`${styles.row} ${isSelected ? styles.rowSelected : ''} ${allowed ? '' : styles.rowDisabled}`}
        role="option"
        aria-selected={isSelected}
        aria-disabled={allowed ? undefined : true}
        tabIndex={allowed ? 0 : -1}
        data-kind={row.kind}
        data-name={row.name}
        onClick={() => choose(row)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            choose(row);
          }
        }}
      >
        <span className={`${styles.kindTile} ${row.kind === 'story' ? styles.kindStory : styles.kindScene}`}>
          <Icon name={row.kind} size={20} />
          <span className={styles.srOnly}>{row.kind === 'story' ? 'Story' : 'Scene'}</span>
        </span>
        <span className={styles.rowMain}>
          <span className={styles.rowName}>{searching ? highlight(row.name, query) : row.name}</span>
          <span className={`${styles.status} ${TONE_CLASS[st.tone]}`}>
            <span className={styles.dot} aria-hidden="true" />
            {st.text}
          </span>
        </span>
        {searching && <span className={styles.rowPath}>{folderPathText(row.folderId)}</span>}
        <span className={styles.check} aria-hidden="true">
          <Icon name="check" size={12} strokeWidth={4} />
        </span>
      </li>
    );
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerText}>
          <Eyebrow />
          <h1 className={styles.title}>{title}</h1>
          {hostLabel && (
            <p className={styles.subtitle}>
              to add to <span className={styles.hostName}>{hostLabel}</span>
            </p>
          )}
        </div>
        <div className={styles.account} title={user.email}>
          <span className={styles.avatar} aria-hidden="true">
            {(user.email?.trim()[0] ?? '?').toUpperCase()}
          </span>
          <span className={styles.accountEmail}>{user.email}</span>
        </div>
      </header>

      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <Icon name="search" />
          <input
            ref={searchRef}
            id="picker-search"
            type="search"
            className={styles.searchInput}
            placeholder="Search all folders"
            aria-label="Search all folders"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {searching && (
            <button type="button" className={styles.clearBtn} aria-label="Clear search" onClick={clearSearch}>
              <Icon name="clear" size={16} />
            </button>
          )}
        </div>
        {bothKinds && (
          <div className={styles.tabs} role="group" aria-label="Show">
            {TABS.map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={`${styles.tab} ${tab === value ? styles.tabOn : ''}`}
                aria-pressed={tab === value}
                onClick={() => setTab(value)}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {searching ? (
        <p className={styles.where} role="status" data-testid="picker-result-count">
          {resultCount(resultTotal)}
        </p>
      ) : (
        <nav className={styles.where} aria-label="Folder path" data-testid="picker-breadcrumb">
          <Icon name="home" size={16} />
          {path.length === 0 ? (
            <span className={styles.crumbCurrent} aria-current="page">
              All content
            </span>
          ) : (
            <button type="button" className={styles.crumb} onClick={() => setFolderId(null)}>
              All content
            </button>
          )}
          {path.map((f, i) => (
            <React.Fragment key={f.id}>
              <span className={styles.crumbSep} aria-hidden="true">
                ›
              </span>
              {i === path.length - 1 ? (
                <span className={styles.crumbCurrent} aria-current="page">
                  {f.name}
                </span>
              ) : (
                <button type="button" className={styles.crumb} onClick={() => setFolderId(f.id)}>
                  {f.name}
                </button>
              )}
            </React.Fragment>
          ))}
        </nav>
      )}

      <div className={styles.scroll}>
        {fetchError && (
          <p className={styles.loadError} role="alert">
            {fetchError}
          </p>
        )}

        {!data && !fetchError && (
          <div className={styles.spinner} role="status">
            <span className={styles.srOnly}>Loading…</span>
          </div>
        )}

        {nothingListed && <p className={styles.empty}>{searching ? 'Nothing matches your search.' : 'This folder is empty.'}</p>}

        {visibleFolders.length > 0 && (
          <section className={styles.section} aria-labelledby="picker-sec-folders">
            <h2 id="picker-sec-folders" className={styles.sectionLabel}>
              Folders
            </h2>
            <div className={styles.folderGrid}>
              {visibleFolders.map((f) => (
                <button
                  key={`folder-${f.id}`}
                  type="button"
                  className={styles.folder}
                  data-kind="folder"
                  data-name={f.name}
                  onClick={() => setFolderId(f.id)}
                >
                  <Icon name="folder" size={20} className={styles.folderIcon} />
                  <span className={styles.folderName}>{f.name}</span>
                  <span className={styles.folderCount}>{itemCount(counts.get(f.id) ?? 0)}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {resultTotal > 0 && (
          <ul className={styles.listbox} role="listbox" aria-label="Stories and scenes">
            {storyRows.length > 0 && (
              <li role="presentation" className={styles.section}>
                <h2 id="picker-sec-stories" className={styles.sectionLabel}>
                  Stories
                </h2>
                <ul role="group" aria-labelledby="picker-sec-stories" className={styles.rows}>
                  {storyRows.map(renderRow)}
                </ul>
              </li>
            )}
            {sceneRows.length > 0 && (
              <li role="presentation" className={styles.section}>
                <h2 id="picker-sec-scenes" className={styles.sectionLabel}>
                  Scenes
                </h2>
                <ul role="group" aria-labelledby="picker-sec-scenes" className={styles.rows}>
                  {sceneRows.map(renderRow)}
                </ul>
              </li>
            )}
          </ul>
        )}
      </div>

      {failure ? (
        <div className={styles.callout}>
          <div className={styles.failure} role="alert" data-testid="picker-publish-error">
            <p className={styles.failureTitle}>
              <Icon name="alert" size={18} />
              It can't be published yet
            </p>
            <ul className={styles.reasons}>
              {failure.reasons.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
            {failure.editUrl && (
              <a className={styles.fixLink} href={failure.editUrl} target="_blank" rel="noopener noreferrer">
                Fix it in Slizzart Stories
                <Icon name="external" size={14} />
              </a>
            )}
          </div>
          <p className={styles.calloutNote}>
            Nothing was sent to {hostOrApp}. Pick something else, or fix this {kindWord} and try again.
          </p>
        </div>
      ) : (
        needsPublish && (
          <div className={styles.callout}>
            <p className={styles.note} data-testid="picker-publish-note">
              <Icon name="info" size={16} />
              {selectedRow.published
                ? `This ${kindWord}'s published files are missing on this server. Selecting it publishes it again.`
                : `This ${kindWord} isn't published yet. Selecting it publishes version 1 first, so ${hostOrApp} has something to play.`}
            </p>
          </div>
        )
      )}

      <footer className={styles.footer}>
        <div className={styles.footerSel} data-testid="picker-selection">
          <span className={styles.footerLabel}>{selectedRow ? 'Selected' : 'Nothing selected'}</span>
          <span className={styles.footerName}>{selectedRow ? selectedRow.name : pickHint}</span>
        </div>
        <div className={styles.footerActions}>
          <button className={styles.plainBtn} onClick={cancel} type="button" disabled={publishing}>
            Cancel
          </button>
          <button
            className={styles.primaryBtn}
            onClick={confirm}
            disabled={!selectedRow || publishing}
            aria-busy={publishing}
            type="button"
          >
            {publishing && <span className={styles.btnSpinner} aria-hidden="true" />}
            {confirmLabel}
          </button>
        </div>
      </footer>
    </div>
  );
};

export default Picker;
